import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, WritableSignal, signal } from '@angular/core';
import { User } from '../entities/user';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { EnvironmentConfigService } from './environment-config.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  userApiUrl: string = `${EnvironmentConfigService.getApiGatewayConfig().USER_ENDPOINT}/users`;
  demoRequestApiUrl: string = `${EnvironmentConfigService.getApiGatewayConfig().USER_ENDPOINT}/demos`;
  userSearchUrl: string = `${this.userApiUrl}/search`;

  showAuthControls: WritableSignal<boolean> = signal(true);
  loggedIn: WritableSignal<boolean> = signal(false);
  loggedInUser: WritableSignal<User> = signal({});
  azLoggedInUser: WritableSignal<any> = signal({});

  constructor(private http: HttpClient,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalAuthService: MsalService) { }

  get isLoggedIn() {
    return this.loggedIn();
  }

  get userData() {
    return this.msalAuthService.instance.getActiveAccount();
  }

  setLoggedIn(isLoggedIn: boolean) {
    this.loggedIn.set(isLoggedIn);
  }

  setUserData(userData: User) {
    this.loggedInUser.set(userData);
  }

  getUsers() {
    return this.http.get<User>(this.userApiUrl);
  }

  getUserByEmail(email: string) {
    const params = new HttpParams()
      .set('email', email);
    return this.http.get<User>(`${this.userApiUrl}/search`, { params });
  }

  registerUser(user: User) {
    return this.http.post(this.userApiUrl, user);
  }

  updateUser(user: User) {
    return this.http.put(`${this.userApiUrl}/${user.id}`, user);
  }

  requestDemo(user: User) {
    return this.http.post(this.demoRequestApiUrl, user)
  }

  login(username: string) {
    let queryParams = new HttpParams();
    queryParams = queryParams.append("email",username);

    return this.http.get<User[]>(this.userSearchUrl, {params:queryParams});
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest){
      this.msalAuthService.loginRedirect({...this.msalGuardConfig.authRequest} as RedirectRequest);
    } else {
      this.msalAuthService.loginRedirect();
    }
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.msalAuthService.instance.getActiveAccount();

    if (!activeAccount && this.msalAuthService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalAuthService.instance.getAllAccounts();
      this.msalAuthService.instance.setActiveAccount(accounts[0]);
    }
  }

  logout(popup?: boolean) {
    if (popup) {
      this.msalAuthService.logoutPopup({
        mainWindowRedirectUri: "/"
      });
    } else {
      this.msalAuthService.logoutRedirect();
    }
  }

}
